// Draft copy per outcome code (docs/02-Game-Design/Outcomes & Feedback.md).
// The client renders feedback from the code, never from its own reasoning.
import type { OutcomeCode, PlayLevel } from '@wits/core';

export interface OutcomeContext {
  word?: string;
  n?: number;
  points?: number;
}

export function outcomeText(code: OutcomeCode | 'error', ctx: OutcomeContext = {}): string | null {
  switch (code) {
    case 'refused_locked':
      return `That star already belongs to ${ctx.word ?? 'a constellation'}.`;
    case 'refused_blocked':
      return 'A line is in the way.';
    case 'refused_no_line':
      return "Lines can't cross.";
    case 'too_short':
      return `Constellations need at least ${ctx.n ?? 4} stars.`;
    case 'not_a_word':
      return ctx.word ? `${ctx.word} is not a valid word.` : 'Not a valid word.';
    case 'not_in_solution':
      return ctx.word ? `${ctx.word} is not part of the solution.` : 'Not part of the solution.';
    case 'already_spelled':
      return `${ctx.word ?? 'That word'} is already in your sky.`;
    case 'accepted':
      return ctx.points !== undefined ? `+${ctx.points}` : null;
    case 'accepted_level_complete':
      return 'The sky is complete.';
    case 'play_finished':
      return 'Your sky is written.';
    case 'replay_mismatch':
      return "We couldn't verify this play.";
    case 'error':
      return 'The sky is out of reach. Try again.';
    default:
      return null;
  }
}

export function finishConfirmText(points: number): string {
  return `Finish with ${points} points? There's no second try today.`;
}

export function removeConfirmText(word: string): string {
  return `Return ${word} to the stars?`;
}

/**
 * The level's goal, in the feedback spot until the first star is selected
 * (docs/02-Game-Design/Tutorial.md). A Puzzle counts the words still to find;
 * an Assemblage asks for every star (docs/02-Game-Design/Assemblage Mode.md).
 */
export function goalHint(level: PlayLevel, unfound: number): string {
  if (level.mode === 'apogee') return 'Spell words to earn points.';
  if (level.mode === 'assemblage') return 'Use every star.';
  const one = unfound === 1;
  const words = `${unfound} ${one ? 'word' : 'words'}`;
  switch (level.puzzle.type) {
    case 'category':
      return `Find ${words} that ${one ? 'fits' : 'fit'} the ${level.puzzle.category} category.`;
    case 'quotation':
      return `Find the ${words} that ${one ? 'completes' : 'complete'} the quote.`;
    default:
      return `Find the ${words} that ${one ? 'completes' : 'complete'} the phrase.`;
  }
}
